import { useState } from 'react'
import type { ComponentProgressDetailView, OverrideQuizAnswerRequest } from '@/types/coaching'
import { PartialDataFallback } from './parts/PartialDataFallback'
import { AdultQuestionView } from './parts/AdultQuestionView'
import { AnswerEditButton, AnswerEditCancelButton } from './parts/AnswerEditButton'
import { SolutionPreviewToggle } from './parts/SolutionPreviewToggle'
import { QuestionChipGrid, type QuestionChipItem } from './parts/QuestionChipGrid'
import { isSubjectiveQuizType } from './parts/SubjectiveGradeSelect'
import { hasSubmittedAnswer } from './parts/answerArray'
import { useSolutionPreviewState } from './parts/useSolutionPreviewState'
import { getQuizReviewMetadata, QuizReviewMetadata } from './parts/QuizReviewMetadata'

interface Props {
  component: ComponentProgressDetailView
  canEdit: boolean
  onOverride: (body: Omit<OverrideQuizAnswerRequest, 'lessonId'>) => void | Promise<void>
  isSubmitting?: boolean
  draftResetKey?: number
  onDraftStateChange?: (state: { dirty: boolean; saving: boolean }) => void
}

// 문제 풀기(quiz) 상세 = 문제 네비게이션 + 문제 화면(학습자/코칭 분리 제거).
// 제출 답안이 있으면 결과 뷰가 기본이고 [답안 편집]으로만 풀이 UI를 연다.
// 답안이 없으면 풀이 UI가 기본이며, 정답 확인 후 결과 뷰로 전환한다.
export function QuizCoachingDetail({ component, canEdit, onOverride, isSubmitting, draftResetKey, onDraftStateChange }: Props) {
  const quiz = component.quiz
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null)
  const [editingQuizIds, setEditingQuizIds] = useState<Set<string>>(new Set())
  const [locallySubmittedIds, setLocallySubmittedIds] = useState<Set<string>>(new Set())
  const {
    isOpen: isPreviewOpen,
    open: openPreview,
    close: closePreview,
    rememberDraft,
    getDraft,
    clear: clearPreview,
  } = useSolutionPreviewState('session', component.configComponentId, {
    draftResetKey, onDraftStateChange, saving: isSubmitting,
    savedAnswers: Object.fromEntries((quiz?.questions ?? []).map((question) => [question.quizId, question.studentAnswer])),
  })

  if (!quiz) return <PartialDataFallback type="quiz" />
  if (!quiz.questions.length) return <PartialDataFallback type="quiz" />

  const currentId = selectedQuizId ?? quiz.questions[0].quizId
  const current = quiz.questions.find((q) => q.quizId === currentId) ?? quiz.questions[0]
  const isSessionComplete = component.status === 'completed'

  const chips: QuestionChipItem[] = quiz.questions.map((q, i) => {
    const hasAnswer = q.isOfflineCompleted || hasSubmittedAnswer(q.studentAnswer)
    const state: QuestionChipItem['state'] = !isSessionComplete
      ? hasAnswer ? 'pending' : 'unanswered'
      : q.isCorrect == null
        ? hasAnswer
          ? 'pending' // 답안 제출 완료 + 미채점: 오프라인 입력 또는 온라인 서술형
          : 'unanswered'
        : q.isCorrect
          ? 'correct'
          : 'incorrect'

    return {
      quizId: q.quizId,
      label: `Q${i + 1}`,
      state,
    }
  })

  // AIQ 진단(test) 모드는 재채점/수정 범위 밖 → 항상 결과 뷰
  // 학습자 세션이 진행 중인 동안 교사는 답안을 바꾸거나 제출하지 않고
  // 정답·해설 preview만 확인한다. 완료 후에만 기존 풀이/답안 편집을 허용한다.
  const isPreviewOnly = !isSessionComplete && quiz.solveMode !== 'test'
  const canSolve = canEdit && !isPreviewOnly && quiz.solveMode !== 'test'
  const isSubjective = isSubjectiveQuizType(current.questionSnapshot)
  const hasAnswer = hasSubmittedAnswer(current.studentAnswer) || locallySubmittedIds.has(current.quizId)
  const showPreview = isPreviewOnly || (canSolve && !hasAnswer && isPreviewOpen(current.quizId))
  const showSolver = canSolve && !showPreview && (editingQuizIds.has(current.quizId) || !hasAnswer)
  const previewPanelId = `solution-preview-session-${component.configComponentId}-${current.quizId}`
  const { correctCriteria, checkPoint } = getQuizReviewMetadata(current.questionSnapshot)

  const selectQuestion = (id: string) => {
    closePreview(current.quizId)
    setSelectedQuizId(id)
  }

  const startEditing = () => {
    setEditingQuizIds((prev) => new Set(prev).add(current.quizId))
  }

  const cancelEditing = () => {
    setEditingQuizIds((prev) => {
      const next = new Set(prev)
      next.delete(current.quizId)
      return next
    })
  }

  const finishEditing = () => {
    setEditingQuizIds((prev) => {
      const next = new Set(prev)
      next.delete(current.quizId)
      return next
    })
    setLocallySubmittedIds((prev) => new Set(prev).add(current.quizId))
    clearPreview(current.quizId)
  }

  const handleSolve = async (answers: string[]) => {
    try { const submission = onOverride({
      configComponentId: component.configComponentId,
      quizId: current.quizId,
      isCorrect: current.isCorrect ?? false, // answers 동반 시 백엔드가 재채점(이 값은 무시됨)
      scope: 'session',
      answers,
      appliedVars: current.appliedVars,
    })
    if (submission) await submission
    finishEditing()
    } catch { /* Mutation reports the error; keep the answer draft available for retry. */ return }
  }

  const handleSubjectiveGrade = async (grade: { isCorrect: boolean; answers: string[] }) => {
    try { const submission = onOverride({
      configComponentId: component.configComponentId,
      quizId: current.quizId,
      isCorrect: grade.isCorrect,
      scope: 'session',
      answers: grade.answers,
      appliedVars: current.appliedVars,
      manualGrade: true,
    })
    if (submission) await submission
    finishEditing()
    } catch { /* Mutation reports the error; keep the answer draft available for retry. */ return }
  }

  return (
    <div className="space-y-3">
      <QuestionChipGrid
        items={chips}
        selectedQuizId={current.quizId}
        onSelect={selectQuestion}
      />
      {canSolve && hasAnswer ? (
        <div className="flex justify-end">
          {showSolver ? (
            <AnswerEditCancelButton onClick={cancelEditing} disabled={isSubmitting} />
          ) : (
            <AnswerEditButton onClick={startEditing} disabled={isSubmitting} />
          )}
        </div>
      ) : null}
      {canSolve && !hasAnswer ? (
        <div className="flex justify-end">
          <SolutionPreviewToggle
            expanded={showPreview}
            controlsId={previewPanelId}
            onClick={() => {
              if (showPreview) {
                closePreview(current.quizId)
              } else {
                openPreview(current.quizId)
              }
            }}
            disabled={isSubmitting}
          />
        </div>
      ) : null}
      {showPreview ? (
        <div id={previewPanelId}>
          <AdultQuestionView
            key={`preview-${current.quizId}`}
            mode="preview"
            quizId={current.quizId}
            order={current.order ?? 0}
            questionSnapshot={current.questionSnapshot}
            correctAnswer={current.correctAnswer}
            explanation={current.explanation}
          />
        </div>
      ) : showSolver ? (
        <AdultQuestionView
          key={`solve-${current.quizId}-${isSubjective ? 'subjective' : 'auto'}`}
          mode="solve"
          quizId={current.quizId}
          order={current.order ?? 0}
          questionSnapshot={current.questionSnapshot}
          studentAnswer={getDraft(current.quizId) ?? current.studentAnswer}
          correctAnswer={current.correctAnswer}
          explanation={current.explanation}
          isCorrect={current.isCorrect ?? undefined}
          corrects={current.corrects}
          onSolve={isSubjective ? undefined : handleSolve}
          onSubjectiveGrade={isSubjective ? handleSubjectiveGrade : undefined}
          onAnswerChange={(answers) => rememberDraft(current.quizId, answers)}
          isSubmitting={isSubmitting}
        />
      ) : (
        <AdultQuestionView
          key={`result-${current.quizId}`}
          mode="result"
          quizId={current.quizId}
          order={current.order ?? 0}
          questionSnapshot={current.questionSnapshot}
          studentAnswer={current.studentAnswer}
          correctAnswer={current.correctAnswer}
          explanation={current.explanation}
          isCorrect={current.isCorrect ?? undefined}
          corrects={current.corrects}
        />
      )}
      <QuizReviewMetadata correctCriteria={correctCriteria} checkPoint={checkPoint} />
    </div>
  )
}
